import type { GencowCtx } from "./context.js";
import { buildRealtimeChannelKey } from "./realtime-channel.js";
import type { InferArgs } from "./v.js";

export type RealtimeChannelAuthorizeResult =
  | {
      channelKey?: string;
      ttlSeconds?: number;
      epoch?: string | number;
    }
  | string
  | false
  | null
  | undefined
  | void;

export type RealtimeChannelAuthorizeHandler<TSchema = unknown> = (
  ctx: GencowCtx,
  args: InferArgs<TSchema>,
) => RealtimeChannelAuthorizeResult | Promise<RealtimeChannelAuthorizeResult>;

export type RealtimeChannelKeyBuilder<TSchema = unknown> = (args: InferArgs<TSchema>) => string;

export interface RealtimeChannelDef<TSchema = unknown> {
  name: string;
  argsSchema?: TSchema;
  channelKey?: RealtimeChannelKeyBuilder<TSchema>;
  authorize: RealtimeChannelAuthorizeHandler<TSchema>;
}

export type RealtimeChannelInput<TSchema = unknown> = {
  args?: TSchema;
  channelKey?: RealtimeChannelKeyBuilder<TSchema>;
  authorize: RealtimeChannelAuthorizeHandler<TSchema>;
};

declare global {
  var __gencow_realtimeChannelRegistry: Map<string, RealtimeChannelDef>;
}

if (!globalThis.__gencow_realtimeChannelRegistry) {
  globalThis.__gencow_realtimeChannelRegistry = new Map();
}

export const realtimeChannelRegistry = globalThis.__gencow_realtimeChannelRegistry;

export function defineRealtimeChannels<const TChannels extends Record<string, RealtimeChannelInput>>(
  channels: TChannels,
): TChannels {
  for (const [name, def] of Object.entries(channels)) {
    realtimeChannelRegistry.set(name, {
      name,
      argsSchema: def.args,
      channelKey: def.channelKey as RealtimeChannelKeyBuilder | undefined,
      authorize: def.authorize as RealtimeChannelAuthorizeHandler,
    });
  }
  return channels;
}

export function getRealtimeChannelDef(name: string): RealtimeChannelDef | undefined {
  return realtimeChannelRegistry.get(name);
}

export function getRegisteredRealtimeChannels(): string[] {
  return [...realtimeChannelRegistry.keys()];
}

export function resolveRealtimeChannelKey(params: {
  channel: string;
  args?: unknown;
  result?: RealtimeChannelAuthorizeResult;
}): string | null {
  if (params.result === false || params.result === null) return null;
  if (typeof params.result === "string") return params.result.length > 0 ? params.result : null;
  if (params.result && typeof params.result === "object" && "channelKey" in params.result) {
    const channelKey = params.result.channelKey;
    if (channelKey !== undefined) return typeof channelKey === "string" && channelKey.length > 0 ? channelKey : null;
  }
  const def = getRealtimeChannelDef(params.channel);
  const channelKey = def?.channelKey?.(params.args as never);
  if (channelKey !== undefined) return typeof channelKey === "string" && channelKey.length > 0 ? channelKey : null;
  return buildRealtimeChannelKey(params.channel, params.args);
}

export function resetRealtimeChannelRegistryForTests(): void {
  realtimeChannelRegistry.clear();
}
